import React from 'react'

/**
 * Represents a parsed content fragment that can be either a React element or string
 */
export type ParsedContentFragment = React.ReactElement | string

/**
 * Replace placeholders in a string with values from a replacement object
 * Placeholders are in the format {key} and will be replaced with values[key]
 *
 * @param template - The string containing placeholders like "Hello {name}"
 * @param values - Object containing replacement values like { name: "World" }
 * @returns String with placeholders replaced
 */
export function replacePlaceholders(template: string, values: Record<string, string>): string {
  return template.replace(/\{([^}]+)\}/g, (match, key) => {
    return values[key] !== undefined ? values[key] : match
  })
}

/**
 * Replace placeholders in an object's string properties recursively
 * Handles nested objects and arrays, replacing any string values that contain placeholders
 *
 * @param obj - The object to process (can be any type)
 * @param values - Object containing replacement values
 * @returns New object with placeholders replaced in all string properties
 */
export function replaceObjectPlaceholders<T>(obj: T, values: Record<string, string>): T {
  if (obj === null || obj === undefined) {
    return obj
  }

  if (typeof obj === 'string') {
    return replacePlaceholders(obj, values) as T
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => replaceObjectPlaceholders(item, values)) as T
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(obj)) {
      result[key] = replaceObjectPlaceholders(value, values)
    }
    return result as T
  }

  return obj
}

/**
 * Component renderer function type for custom tag rendering
 */
export interface ComponentRenderer {
  (content: string, index: number): React.ReactElement
}

/**
 * Mapping of HTML-like tag names to their React component renderers
 */
export interface ComponentMap {
  strong?: ComponentRenderer
  span?: ComponentRenderer
  i?: ComponentRenderer
  em?: ComponentRenderer
}

/**
 * Parse message content and convert HTML-like markup to React elements
 * Handles <strong>text</strong> and <span>text</span> markup from banner configurations
 *
 * @param message - The message string containing markup like "Get <strong>FREE delivery</strong> in <span>2 hrs</span>"
 * @param components - Optional mapping object of tag names to component renderers
 * @returns Array of React elements and strings that can be rendered
 *
 * @example
 * ```tsx
 * const message = "Get <strong>FREE delivery tomorrow</strong>. Order in the next <span>2 hrs 30 min</span>"
 * const content = parseMessageContent(message, {
 *   strong: (content, index) => <Strong key={index}>{content}</Strong>,
 *   span: (content, index) => <Text key={index} fontWeight="SemiBold">{content}</Text>
 * })
 * return <div>{content}</div>
 * ```
 */
export function parseMessageContent(
  message: string,
  components?: ComponentMap,
): ParsedContentFragment[] {
  if (!message || typeof message !== 'string') {
    return []
  }

  // Split by properly matched tags, keeping the delimiters
  const parts = message.split(
    /(<strong>.*?<\/strong>|<span>.*?<\/span>|<em>.*?<\/em>|<i>.*?<\/i>)/g,
  )

  return parts
    .filter((part) => part.length > 0) // Remove empty strings
    .map((part, index) => {
      // Check for supported tags dynamically
      for (const tagName of ['strong', 'span', 'em', 'i']) {
        const openTag = `<${tagName}>`
        const closeTag = `</${tagName}>`

        if (part.startsWith(openTag) && part.endsWith(closeTag)) {
          const content = part.replace(new RegExp(`</?${tagName}>`, 'g'), '')
          const renderer = components && components[tagName as keyof ComponentMap]

          if (renderer) {
            return renderer(content, index)
          }

          return React.createElement(tagName, { key: index }, content)
        }
      }

      // Return plain text if no tag match
      return part
    })
}
